import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type FormEvent,
} from 'react';
import { Button } from '@openai/apps-sdk-ui/components/Button';
import { Input } from '@openai/apps-sdk-ui/components/Input';
import { Avatar } from '@openai/apps-sdk-ui/components/Avatar';
import {
  ArrowRight,
  Bell,
  Bills,
  CalendarToday,
  CheckCircle,
  CloseBold,
  Keyboard,
  Members,
  Menu,
  NotebookCheck,
  Plus,
  Settings as SettingsIcon,
  Terminal as TerminalIcon,
} from '@openai/apps-sdk-ui/components/Icon';
import type {
  Agenda,
  Channel,
  NewPersonalItem,
  PersonalItem,
  PersonalItemKind,
  PersonalItemPatch,
  SearchResults,
  RecurrenceConfig,
  Recurrence,
  UnreadCounts,
  Message,
  NewProject,
  NewTask,
  Project,
  Task,
  TaskPatch,
  User,
  Workspace,
  FollowUpSource,
  AdminAccount,
} from '../shared/types';
import { DISABLED_ATOMIC_PERMISSIONS } from '../shared/types';
import { COMMANDS, parseCommand } from '../shared/commands';
import { validatedTimeZone } from '../shared/datetime';
import { api, APIError, invalidateAccountRequests, errorMessage } from './api';
import { googleCompletionMessage } from './google-auth';
import type { AssistantTurn } from './Assistant';
import type {
  AiScope,
  AiSource,
  AiProposal,
  AiMessageDraft,
  AiAskResponse,
  AiConfiguration,
  AiConfirmResponse,
} from '../shared/ai';
import { BrandIdentity } from './Brand';
import { ThemeToggle } from './theme';
import { Tasks, TaskEditor, STATUS_LABEL } from './Tasks';
import { Projects, ProjectSetup, ProjectBoard } from './Projects';
import { CommandBar, Shortcuts, TerminalPage, type CommandEntry } from './Command';
import { TodayPage, LibraryPage, PersonalEditor, SearchPage, ITEM_LABEL } from './Personal';
import type { UpcomingSnapshot } from './Upcoming';
import type { ForgottenSnapshot, ForgottenReview } from './Forgotten';
import type { ProfileDraft } from './Profiles';
import { clearBrowserBinding } from './notifications-client';
import { newlyArrived, playPing } from './message-bell';
import { Empty, ErrorNotice, Field, Kbd, Modal } from './ui';
import { crmPageLabel } from './crm/util';

function atomicResultText(value: unknown): string {
  const serialized = JSON.stringify(value, null, 2);
  if (!serialized) return 'Atomic returned no data.';
  return serialized.length > 18000
    ? `${serialized.slice(0, 18000)}\n\n[Terminal display truncated. Use a narrower Atomic command.]`
    : serialized;
}

function withPageBoundary<P extends object>(Page: ComponentType<P>) {
  return function PageWithBoundary(props: P) {
    return (
      <Suspense
        fallback={
          <div className="loading-content" role="status">
            Loading page…
          </div>
        }
      >
        <Page {...props} />
      </Suspense>
    );
  };
}

const Auth = lazy(() => import('./Auth').then(({ Auth }) => ({ default: Auth })));
const Assistant = lazy(() =>
  import('./Assistant').then(({ Assistant }) => ({ default: Assistant })),
);
const Conversation = withPageBoundary(
  lazy(() => import('./Conversation').then(({ Conversation }) => ({ default: Conversation }))),
);
const UpcomingPage = withPageBoundary(
  lazy(() => import('./Upcoming').then(({ UpcomingPage }) => ({ default: UpcomingPage }))),
);
const ForgottenPage = withPageBoundary(
  lazy(() => import('./Forgotten').then(({ ForgottenPage }) => ({ default: ForgottenPage }))),
);
const ProjectContext = withPageBoundary(
  lazy(() =>
    import('./ProjectContext').then(({ ProjectContext }) => ({ default: ProjectContext })),
  ),
);
const ProfilePage = withPageBoundary(
  lazy(() => import('./Profiles').then(({ ProfilePage }) => ({ default: ProfilePage }))),
);
const AdminPage = withPageBoundary(
  lazy(() => import('./Admin').then(({ AdminPage }) => ({ default: AdminPage }))),
);
const Settings = withPageBoundary(
  lazy(() => import('./Settings').then(({ Settings }) => ({ default: Settings }))),
);
const People = withPageBoundary(
  lazy(() => import('./Settings').then(({ People }) => ({ default: People }))),
);
const CrmApp = withPageBoundary(
  lazy(() => import('./crm/CrmApp').then(({ CrmApp }) => ({ default: CrmApp }))),
);

type DeleteRequest = ({ kind: 'task'; task: Task } | { kind: 'project'; project: Project }) & {
  resolve?: (confirmed: boolean) => void;
};
export function App() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const [user, setUser] = useState<User | null>(null);
  const [authMethod, setAuthMethod] = useState<'password' | 'google' | null>(null);
  const [googleLinked, setGoogleLinked] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [bootstrapRequired, setBootstrapRequired] = useState(false);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [historyUsers, setHistoryUsers] = useState<User[]>([]);
  const historyCache = useRef(historyUsers);
  historyCache.current = historyUsers;
  const deletedUserIds = useRef(new Set<string>());
  const userCache = useRef(user);
  userCache.current = user;
  const rosterCache = useRef(users);
  rosterCache.current = users;
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [items, setItems] = useState<PersonalItem[]>([]);
  const [unread, setUnread] = useState<UnreadCounts>({ channels: {}, people: {} });
  const [unreadError, setUnreadError] = useState('');
  const [personalSyncError, setPersonalSyncError] = useState('');
  const [taskSyncError, setTaskSyncError] = useState('');
  const taskCache = useRef(tasks);
  taskCache.current = tasks;
  const searchOpenSerial = useRef(0);
  const followUpOpenSerial = useRef(0);
  const unreadSerial = useRef(0);
  const unreadBaseline = useRef<Record<string, number> | null>(null);
  const [bellRings, setBellRings] = useState<Record<string, number>>({});
  const [personalEditor, setPersonalEditor] = useState<{
    kind: PersonalItemKind;
    item: PersonalItem | null;
    source?: FollowUpSource;
    title?: string;
    counterparty?: string;
    projectId?: string | null;
  } | null>(null);
  const [personalDelete, setPersonalDelete] = useState<PersonalItem | null>(null);
  const [personalDeleting, setPersonalDeleting] = useState(false);
  const personalDeleteRunning = useRef(false);
  const [personalDeleteError, setPersonalDeleteError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchRun, setSearchRun] = useState(0);
  const [searchRevision, setSearchRevision] = useState(0);
  const deviceTimeZone = useMemo(
    () => validatedTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'),
    [],
  );
  const timeZone = user?.timeZone || deviceTimeZone;
  const [clock, setClock] = useState(Date.now());
  const [visible, setVisible] = useState(!document.hidden);
  useEffect(() => {
    const update = () => {
      setVisible(!document.hidden);
      if (!document.hidden) setClock(Date.now());
    };
    const tick = setInterval(() => {
      if (!document.hidden) setClock(Date.now());
    }, 15000);
    document.addEventListener('visibilitychange', update);
    return () => {
      clearInterval(tick);
      document.removeEventListener('visibilitychange', update);
    };
  }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [path, setPath] = useState(location.pathname);
  const pathCache = useRef(path);
  pathCache.current = path;
  const [routeQuery, setRouteQuery] = useState(location.search);
  const [mobileNav, setMobileNav] = useState(false);
  const [editor, setEditor] = useState<Task | 'new' | null>(null);
  const [newTaskDefaults, setNewTaskDefaults] = useState<{
    projectId: string | null;
    status: Task['status'];
  }>({ projectId: null, status: 'todo' });
  const [channelModal, setChannelModal] = useState(false);
  const [deleteRequest, setDeleteRequest] = useState<DeleteRequest | null>(null);
  const [deleting, setDeleting] = useState(false);
  const deletionRunning = useRef(false);
  const [deleteError, setDeleteError] = useState('');
  const [command, setCommand] = useState('');
  const [commandBusy, setCommandBusy] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const [entries, setEntries] = useState<CommandEntry[]>([]);
  const [assistant, setAssistant] = useState<AssistantTurn | null>(null);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const assistantSerial = useRef(0);
  const assistantController = useRef<AbortController | null>(null);
  useEffect(() => () => assistantController.current?.abort(), []);
  const [messageDraft, setMessageDraft] = useState<(AiMessageDraft & { nonce: number }) | null>(
    null,
  );

  const commandRef = useRef<HTMLInputElement>(null);
  const entryId = useRef(0);
  const accountGeneration = useRef(0);
  const dataRevision = useRef(0);
  const canonicalReadRevision = useRef(0);
  const deletedProjectIds = useRef(new Set<string>());
  const deletedItemIds = useRef(new Set<string>());
  const deletedTaskIds = useRef(new Set<string>());
  const recurrenceRules = useRef(new Map<string, Recurrence>());
  const deletedRuleIds = useRef(new Set<string>());
  const commandRunning = useRef(false);
  const refreshRequest = useRef<{ generation: number; promise: Promise<void> } | null>(null);
  const [toast, setToast] = useState<{ text: string; error?: boolean } | null>(null);
  const navigate = useCallback((next: string) => {
    followUpOpenSerial.current++;
    historyPush(next);
    const target = new URL(next, location.origin);
    setPath(target.pathname);
    setRouteQuery(target.search);
    setMobileNav(false);
    if (target.hash)
      requestAnimationFrame(() =>
        document.getElementById(target.hash.slice(1))?.scrollIntoView({ block: 'start' }),
      );
  }, []);
  function changeTaskEditor(next: Parameters<typeof setEditor>[0]) {
    followUpOpenSerial.current++;
    setEditor(next);
  }
  function changePersonalEditor(next: Parameters<typeof setPersonalEditor>[0]) {
    followUpOpenSerial.current++;
    setPersonalEditor(next);
  }
  const notify = useCallback((text: string) => setToast({ text }), []);
  const resetAccount = useCallback(() => {
    accountGeneration.current++;
    assistantSerial.current++;
    assistantController.current?.abort();
    assistantController.current = null;
    setAssistant(null);
    setAssistantOpen(false);
    setMessageDraft(null);
    dataRevision.current++;
    canonicalReadRevision.current++;
    deletedProjectIds.current.clear();
    deletedItemIds.current.clear();
    deletedTaskIds.current.clear();
    recurrenceRules.current.clear();
    deletedRuleIds.current.clear();
    invalidateAccountRequests();
    commandRunning.current = false;
    setUser(null);
    setAuthMethod(null);
    setGoogleLinked(false);
    userCache.current = null;
    setWorkspace(null);
    setUsers([]);
    rosterCache.current = [];
    historyCache.current = [];
    setHistoryUsers([]);
    deletedUserIds.current.clear();
    setTasks([]);
    setProjects([]);
    setChannels([]);
    setItems([]);
    setUnread({ channels: {}, people: {} });
    setUnreadError('');
    setPersonalSyncError('');
    setTaskSyncError('');
    searchOpenSerial.current++;
    followUpOpenSerial.current++;
    unreadSerial.current++;
    unreadBaseline.current = null;
    setBellRings({});
    changePersonalEditor(null);
    setPersonalDelete(null);
    setPersonalDeleting(false);
    personalDeleteRunning.current = false;
    setPersonalDeleteError('');
    setSearchQuery('');
    setSearchRun(0);
    setSearchRevision(0);
    setEntries([]);
    setHistory([]);
    setCommand('');
    setCommandBusy(false);
    changeTaskEditor(null);
    setNewTaskDefaults({ projectId: null, status: 'todo' });
    setChannelModal(false);
    setMobileNav(false);
    setError('');
    setToast(null);
    setLoading(true);
    setDeleting(false);
    deletionRunning.current = false;
    setDeleteError('');
    setDeleteRequest((request) => {
      request?.resolve?.(false);
      return null;
    });
  }, []);
  useEffect(() => {
    const expired = () => {
      resetAccount();
      setToast({ text: 'Your session ended. Sign in to continue.', error: true });
    };
    window.addEventListener('terminal:session-expired', expired);
    return () => window.removeEventListener('terminal:session-expired', expired);
  }, [resetAccount]);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    const pop = () => {
      followUpOpenSerial.current++;
      setPath(location.pathname);
      setRouteQuery(location.search);
    };
    window.addEventListener('popstate', pop);
    return () => window.removeEventListener('popstate', pop);
  }, []);
  const loadSession = useCallback(async () => {
    const generation = accountGeneration.current;
    setSessionError('');
    try {
      const result = await api<{
        user: User | null;
        needsSetup: boolean;
        bootstrapRequired: boolean;
        authMethod: 'password' | 'google' | null;
        googleLinked: boolean;
      }>('/session');
      if (generation !== accountGeneration.current) return;
      const completion = location.pathname === '/auth/google/complete';
      if (completion || (!!userCache.current && userCache.current.id !== result.user?.id))
        resetAccount();
      setUser((previous) =>
        result.user && previous?.id === result.user.id
          ? mergeProfile(result.user, previous)
          : result.user,
      );
      setAuthMethod(result.authMethod);
      setGoogleLinked(result.googleLinked);
      if (completion) {
        const query = new URLSearchParams(location.search);
        const outcome = query.get('result');
        const succeeded =
          !query.has('error') &&
          ['signed-in', 'linked', 'joined'].includes(outcome || '') &&
          !!result.user;
        const destination = succeeded
          ? outcome === 'joined'
            ? '/profile?welcome=1'
            : outcome === 'linked'
              ? '/profile'
              : '/tasks'
          : result.user
            ? '/profile'
            : '/';
        window.history.replaceState(null, '', destination);
        const target = new URL(destination, location.origin);
        setPath(target.pathname);
        setRouteQuery(target.search);
        if (!succeeded)
          setToast({ text: googleCompletionMessage(query.get('error')), error: true });
        else if (outcome === 'linked') setToast({ text: 'Google account linked.' });
      }
      setNeedsSetup(result.needsSetup);
      setBootstrapRequired(result.bootstrapRequired);
      setSessionReady(true);
    } catch (error) {
      if (generation === accountGeneration.current) setSessionError(errorMessage(error));
    }
  }, [resetAccount]);
  useEffect(() => {
    void loadSession();
  }, [loadSession]);
  const refresh = useCallback(async () => {
    const generation = accountGeneration.current;
    if (refreshRequest.current?.generation === generation) return refreshRequest.current.promise;
    const revision = dataRevision.current;
    const request = (async () => {
      const [workspaceResult, taskResult, channelResult, projectResult, itemResult] =
        await Promise.all([
          api<{ workspace: Workspace; users: User[]; historyUsers: User[] }>('/workspace'),
          api<{ tasks: Task[] }>('/tasks'),
          api<{ channels: Channel[] }>('/channels'),
          api<{ projects: Project[] }>('/projects'),
          api<{ items: PersonalItem[] }>('/items'),
        ]);
      if (generation !== accountGeneration.current || revision !== dataRevision.current) return;
      canonicalReadRevision.current++;
      setWorkspace(workspaceResult.workspace);
      cacheRoster(workspaceResult.users, workspaceResult.historyUsers);
      const freshTasks = taskResult.tasks
        .filter((task) => !deletedTaskIds.current.has(task.id))
        .map(withoutDeletedProject);
      taskCache.current = freshTasks;
      setTasks(freshTasks);
      setChannels(channelResult.channels);
      setProjects(projectResult.projects);
      const freshItems = itemResult.items
        .filter((item) => !deletedItemIds.current.has(item.id))
        .map((item) => safePersonalItem(item));
      setItems((previous) =>
        freshItems
          .filter((item) => !deletedItemIds.current.has(item.id))
          .map((item) => {
            const current = previous.find((existing) => existing.id === item.id);
            const saved = current && current.version > item.version ? current : item;
            return safePersonalItem(saved, false);
          }),
      );
      setError('');
      setPersonalSyncError('');
      setTaskSyncError('');
      setLoading(false);
    })();
    refreshRequest.current = { generation, promise: request };
    try {
      await request;
    } finally {
      if (refreshRequest.current?.promise === request) refreshRequest.current = null;
    }
  }, []);
  useEffect(() => {
    if (!user) return;
    let active = true;
    const generation = accountGeneration.current;
    const load = async () => {
      try {
        await refresh();
      } catch (error) {
        if (active && generation === accountGeneration.current) {
          setError(errorMessage(error));
          setLoading(false);
        }
      }
    };
    void load();
    const interval = setInterval(() => {
      if (!document.hidden) void load();
    }, 15000);
    const visible = () => {
      if (!document.hidden) void load();
    };
    document.addEventListener('visibilitychange', visible);
    return () => {
      active = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [user?.id, refresh]);
  const refreshUnread = useCallback(async () => {
    const generation = accountGeneration.current;
    const serial = ++unreadSerial.current;
    try {
      const response = await api<UnreadCounts>('/unread');
      if (generation === accountGeneration.current && serial === unreadSerial.current) {
        // The first response after sign-in is the baseline: unread messages that
        // were already waiting show a still bell but do not ring.
        const before = unreadBaseline.current;
        unreadBaseline.current = response.people;
        const arrived = before
          ? newlyArrived(
              before,
              response.people,
              (id) =>
                id === userCache.current?.id ||
                (!document.hidden && pathCache.current === `/people/${id}`),
            )
          : [];
        if (arrived.length) {
          setBellRings((current) => {
            const next = { ...current };
            for (const id of arrived) next[id] = (next[id] || 0) + 1;
            return next;
          });
          playPing();
        }
        setUnread(response);
        setUnreadError('');
      }
    } catch (error) {
      if (generation === accountGeneration.current && serial === unreadSerial.current)
        setUnreadError(errorMessage(error));
    }
  }, []);
  useEffect(() => {
    if (!user) return;
    void refreshUnread();
    const timer = setInterval(() => {
      if (!document.hidden) void refreshUnread();
    }, 5000);
    const show = () => {
      if (!document.hidden) void refreshUnread();
    };
    document.addEventListener('visibilitychange', show);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', show);
    };
  }, [user?.id, refreshUnread]);
  async function acknowledgeMessages(ids: string[]) {
    const generation = accountGeneration.current;
    await api('/messages/read', 'POST', { ids });
    if (generation === accountGeneration.current) await refreshUnread();
  }
  useEffect(() => {
    const keyboard = (event: globalThis.KeyboardEvent) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === 'k' &&
        user &&
        path !== '/join'
      ) {
        event.preventDefault();
        commandRef.current?.focus();
      }
      if (event.key === 'Escape') setMobileNav(false);
      const target = event.target as HTMLElement;
      if (
        event.key.toLowerCase() === 'c' &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        user &&
        !document.querySelector('dialog[open]') &&
        !['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) &&
        !target.isContentEditable &&
        path !== '/join'
      ) {
        event.preventDefault();
        const project = projects.find((item) => path === `/projects/${item.id}`);
        if (!project) navigate('/tasks');
        openNewTask(project?.id || null);
      }
    };
    window.addEventListener('keydown', keyboard);
    return () => window.removeEventListener('keydown', keyboard);
  }, [user, path, projects, navigate]);
  const context = useMemo(
    () => ({ tasks, users, channels, projects, items, timeZone }),
    [tasks, users, channels, projects, items, timeZone],
  );
  function openNewTask(projectId: string | null = null, status: Task['status'] = 'todo') {
    setNewTaskDefaults({ projectId, status });
    changeTaskEditor('new');
  }
  function mergeProfile(incoming: User, known?: User | null): User {
    let saved = known && known.profileVersion > incoming.profileVersion ? known : incoming;
    if (saved.accountStatus === 'deleted') deletedUserIds.current.add(saved.id);
    if (deletedUserIds.current.has(saved.id))
      saved = {
        ...saved,
        name: 'Deleted user',
        email: '',
        jobTitle: '',
        bio: '',
        timeZone: null,
        role: 'member',
        canAdmin: false,
        atomicPermissions: DISABLED_ATOMIC_PERMISSIONS,
        accountStatus: 'deleted',
      };
    else if (saved.accountStatus === 'paused')
      saved = {
        ...saved,
        email: '',
        jobTitle: '',
        bio: '',
        timeZone: null,
        role: 'member',
        canAdmin: false,
        atomicPermissions: DISABLED_ATOMIC_PERMISSIONS,
      };
    return saved;
  }
  function cacheRoster(incoming: User[], history: User[] = []) {
    const rows = [...incoming, ...history].map((profile) =>
      mergeProfile(
        mergeProfile(
          profile,
          [...rosterCache.current, ...historyCache.current].find((row) => row.id === profile.id),
        ),
        userCache.current?.id === profile.id ? userCache.current : null,
      ),
    );
    rosterCache.current = rows.filter((row) => row.accountStatus === 'active');
    historyCache.current = rows.filter((row) => row.accountStatus !== 'active');
    setUsers(rosterCache.current);
    setHistoryUsers(historyCache.current);
    const own = rosterCache.current.find((row) => row.id === userCache.current?.id);
    if (own) {
      userCache.current = own;
      setUser((previous) => (previous?.id === own.id ? mergeProfile(own, previous) : previous));
    }
  }
  function cacheProfile(incoming: User): User {
    const profile = mergeProfile(
      mergeProfile(
        incoming,
        [...rosterCache.current, ...historyCache.current].find((row) => row.id === incoming.id),
      ),
      userCache.current?.id === incoming.id ? userCache.current : null,
    );
    if (profile.accountStatus !== 'active') {
      rosterCache.current = rosterCache.current.filter((row) => row.id !== profile.id);
      setUsers(rosterCache.current);
      historyCache.current = [
        ...historyCache.current.filter((row) => row.id !== profile.id),
        profile,
      ];
      setHistoryUsers(historyCache.current);
      return profile;
    }
    historyCache.current = historyCache.current.filter((row) => row.id !== profile.id);
    setHistoryUsers(historyCache.current);
    rosterCache.current = rosterCache.current.some((row) => row.id === profile.id)
      ? rosterCache.current.map((row) => (row.id === profile.id ? profile : row))
      : [...rosterCache.current, profile];
    setUsers(rosterCache.current);
    if (userCache.current?.id === profile.id) {
      userCache.current = profile;
      setUser(profile);
    }
    return profile;
  }
  async function loadProfile(id: string, isCurrent: () => boolean): Promise<User> {
    const generation = accountGeneration.current;
    const revision = dataRevision.current;
    const readRevision = canonicalReadRevision.current;
    const { profile } = await api<{ profile: User }>(`/profiles/${encodeURIComponent(id)}`);
    if (generation !== accountGeneration.current) throw new Error('Your account changed.');
    if (
      !isCurrent() ||
      revision !== dataRevision.current ||
      readRevision !== canonicalReadRevision.current
    )
      throw new Error('The workspace changed. Refresh this profile to load the latest details.');
    return cacheProfile(profile);
  }
  async function saveProfile(draft: ProfileDraft): Promise<User> {
    const generation = accountGeneration.current;
    try {
      const { profile } = await api<{ profile: User }>('/profile', 'PATCH', draft);
      if (generation !== accountGeneration.current) throw new Error('Your account changed.');
      dataRevision.current++;
      return cacheProfile(profile);
    } catch (error) {
      if (
        generation === accountGeneration.current &&
        error instanceof APIError &&
        error.status === 409
      ) {
        const current = await api<{ profile: User }>('/profiles/me').catch(() => null);
        if (current && generation === accountGeneration.current) cacheProfile(current.profile);
      }
      throw error;
    }
  }
  function reconcileAdminChange(account: AdminAccount) {
    const generation = accountGeneration.current;
    dataRevision.current++;
    if (account.userId && (account.status === 'paused' || account.status === 'deleted')) {
      const known = [...rosterCache.current, ...historyCache.current].find(
        (row) => row.id === account.userId,
      );
      if (account.status === 'deleted') deletedUserIds.current.add(account.userId);
      if (known && account.profileVersion !== null)
        cacheProfile({
          ...known,
          accountStatus: account.status,
          profileVersion: account.profileVersion,
          canAdmin: false,
          atomicPermissions: DISABLED_ATOMIC_PERMISSIONS,
        });
    }
    refreshRequest.current = null;
    void refresh().catch((error) => {
      if (generation === accountGeneration.current)
        setError(
          `The account change was saved, but workspace data could not be refreshed. ${errorMessage(error)}`,
        );
    });
  }
  async function createProject(payload: NewProject) {
    const generation = accountGeneration.current;
    const { project } = await api<{ project: Project }>('/projects', 'POST', payload);
    if (generation !== accountGeneration.current) return;
    if (deletedProjectIds.current.has(project.id)) return;
    dataRevision.current++;
    setProjects((previous) => [
      project,
      ...previous.filter((existing) => existing.id !== project.id),
    ]);
    if (['/projects/new', '/new-project'].includes(location.pathname))
      navigate(`/projects/${project.id}`);
    notify('Project created. Add your first task to the board.');
  }
  async function createTask(payload: NewTask) {
    const generation = accountGeneration.current;
    const { task } = await api<{ task: Task }>('/tasks', 'POST', payload);
    if (generation !== accountGeneration.current) return;
    dataRevision.current++;
    cacheTaskAcks([task]);
    notify('Task created.');
  }
  function normalizeRecurrence<T extends { recurrence: Recurrence | null }>(
    row: T,
    remember = true,
  ): T {
    if (!row.recurrence) return row;
    const known = recurrenceRules.current.get(row.recurrence.id);
    let rule = known && known.version >= row.recurrence.version ? known : row.recurrence;
    if (deletedRuleIds.current.has(rule.id)) rule = { ...rule, active: false };
    if (remember) recurrenceRules.current.set(rule.id, rule);
    return rule === row.recurrence ? row : { ...row, recurrence: rule };
  }
  function rememberDeletedRule(rule: Recurrence) {
    const known = recurrenceRules.current.get(rule.id);
    deletedRuleIds.current.add(rule.id);
    recurrenceRules.current.set(rule.id, {
      ...(known && known.version > rule.version ? known : rule),
      active: false,
      version: Math.max(known?.version || 0, rule.version + 1),
    });
  }
  function safePersonalItem(item: PersonalItem, remember = true): PersonalItem {
    if (item.source?.kind === 'task' && deletedTaskIds.current.has(item.source.id))
      item = { ...item, source: null };
    return normalizeRecurrence(
      item.projectId && deletedProjectIds.current.has(item.projectId)
        ? { ...item, projectId: null }
        : item,
      remember,
    );
  }
  function personalItemNeedsReconciliation(item: PersonalItem) {
    return !!(
      (item.projectId && deletedProjectIds.current.has(item.projectId)) ||
      (item.source?.kind === 'task' && deletedTaskIds.current.has(item.source.id))
    );
  }
  function cacheTaskAcks(incoming: Task[]) {
    const safe = incoming
      .filter((task) => !deletedTaskIds.current.has(task.id))
      .map(withoutDeletedProject);
    const merge = (previous: Task[]) => {
      const map = new Map(previous.map((task) => [task.id, task]));
      for (const task of safe) {
        const current = map.get(task.id);
        if (!current || current.version <= task.version) map.set(task.id, task);
      }
      const result = [
        ...safe.map((task) => map.get(task.id)!),
        ...previous.filter((task) => !safe.some((saved) => saved.id === task.id)),
      ];
      return result.map((task) => normalizeRecurrence(task, false));
    };
    taskCache.current = merge(taskCache.current);
    setTasks(merge);
  }
  async function refreshTaskCache() {
    const generation = accountGeneration.current;
    const revision = dataRevision.current;
    const response = await api<{ tasks: Task[] }>('/tasks');
    if (generation !== accountGeneration.current || revision !== dataRevision.current) return;
    canonicalReadRevision.current++;
    const fresh = response.tasks
      .filter((task) => !deletedTaskIds.current.has(task.id))
      .map(withoutDeletedProject);
    taskCache.current = fresh;
    setTasks(fresh);
    setTaskSyncError('');
  }
  async function reconcileTasks() {
    const generation = accountGeneration.current;
    try {
      await refreshTaskCache();
    } catch {
      if (generation === accountGeneration.current)
        setTaskSyncError(
          'Your change was saved, but tasks could not be refreshed. Retry to load their latest versions.',
        );
    }
  }
  async function updateTask(id: string, patch: TaskPatch) {
    const generation = accountGeneration.current;
    try {
      const response = await api<{ task: Task; nextTask?: Task | null; notice?: string }>(
        `/tasks/${id}`,
        'PATCH',
        {
          ...patch,
          expectedVersion:
            patch.expectedVersion ?? taskCache.current.find((task) => task.id === id)?.version,
        },
      );
      if (generation !== accountGeneration.current) return;
      dataRevision.current++;
      cacheTaskAcks([response.task, ...(response.nextTask ? [response.nextTask] : [])]);
      notify(
        response.notice ||
          (response.nextTask
            ? 'Task completed. Next occurrence created.'
            : patch.status
              ? `Task ${patch.status === 'done' ? 'completed' : patch.status === 'todo' ? 'reopened' : 'started'}.`
              : 'Task updated.'),
      );
    } catch (error) {
      if (
        generation === accountGeneration.current &&
        error instanceof APIError &&
        error.status === 409
      )
        await refreshTaskCache().catch(() => {});
      throw error;
    }
  }
  async function configureTaskRecurrence(
    id: string,
    recurrence: RecurrenceConfig | null,
    expectedVersion: number,
  ): Promise<Task> {
    const generation = accountGeneration.current;
    try {
      const { task, notice } = await api<{ task: Task; notice?: string }>(
        `/tasks/${id}/recurrence`,
        'PATCH',
        { expectedVersion, recurrence },
      );
      if (generation !== accountGeneration.current) throw new Error('Your account changed.');
      dataRevision.current++;
      cacheTaskAcks([task]);
      notify(
        notice ||
          (recurrence
            ? 'Task repeat updated.'
            : 'Future repeats stopped. The current task is kept.'),
      );
      return withoutDeletedProject(task);
    } catch (error) {
      if (
        generation === accountGeneration.current &&
        error instanceof APIError &&
        error.status === 409
      )
        await refreshTaskCache().catch(() => {});
      throw error;
    }
  }
  async function openSearchTask(task: Task) {
    const generation = accountGeneration.current;
    const serial = ++searchOpenSerial.current;
    const revision = dataRevision.current;
    try {
      const response = await api<{ tasks: Task[] }>('/tasks');
      if (generation !== accountGeneration.current || serial !== searchOpenSerial.current) return;
      const freshTasks = response.tasks
        .filter((current) => !deletedTaskIds.current.has(current.id))
        .map(withoutDeletedProject)
        .map((current) => normalizeRecurrence(current, false));
      const fresh = freshTasks.find((current) => current.id === task.id);
      const cached = taskCache.current.find((current) => current.id === task.id);
      if (!fresh || deletedTaskIds.current.has(task.id)) {
        notify('This task is no longer available. Search again to refresh your results.');
        setSearchRevision((previous) => previous + 1);
        return;
      }
      const current = cached && cached.version > fresh.version ? cached : fresh;
      if (revision === dataRevision.current) {
        canonicalReadRevision.current++;
        taskCache.current = freshTasks;
        setTasks(freshTasks);
      }
      changeTaskEditor(withoutDeletedProject(current));
    } catch (error) {
      if (generation === accountGeneration.current && serial === searchOpenSerial.current)
        setToast({ text: errorMessage(error), error: true });
    }
  }
  async function refreshPrivateItems() {
    const generation = accountGeneration.current;
    const revision = dataRevision.current;
    const response = await api<{ items: PersonalItem[] }>('/items');
    if (generation !== accountGeneration.current || revision !== dataRevision.current) return;
    canonicalReadRevision.current++;
    setPersonalSyncError('');
    const freshItems = response.items
      .filter((item) => !deletedItemIds.current.has(item.id))
      .map((item) => safePersonalItem(item));
    setItems((previous) =>
      freshItems
        .filter((item) => !deletedItemIds.current.has(item.id))
        .map((item) => {
          const current = previous.find((existing) => existing.id === item.id);
          const saved = current && current.version > item.version ? current : item;
          return safePersonalItem(saved, false);
        }),
    );
  }
  function cachePersonalItem(item: PersonalItem) {
    if (deletedItemIds.current.has(item.id)) return;
    const saved = safePersonalItem(item);
    setItems((previous) => {
      const current = previous.find((existing) => existing.id === saved.id);
      const chosen = current && current.version > saved.version ? current : saved;
      return [chosen, ...previous.filter((existing) => existing.id !== saved.id)].map((existing) =>
        safePersonalItem(existing, false),
      );
    });
  }
  function reconcilePersonalItems() {
    const generation = accountGeneration.current;
    void refreshPrivateItems().catch(() => {
      if (generation === accountGeneration.current)
        setPersonalSyncError(
          'Your change was saved, but private items could not be refreshed. Retry to load their latest versions.',
        );
    });
  }
  function editPersonalItem(item: PersonalItem) {
    changePersonalEditor({ kind: item.kind, item });
  }
  function createFollowUp(source: FollowUpSource) {
    const task = source.kind === 'task' ? tasks.find((task) => task.id === source.id) : null;
    const channel =
      source.kind === 'channel' ? channels.find((channel) => channel.id === source.id) : null;
    const peer = source.kind === 'dm' ? users.find((peer) => peer.id === source.id) : null;
    if (!task && !channel && !peer) {
      notify('This source is no longer available.');
      return;
    }
    changeTaskEditor(null);
    changePersonalEditor({
      kind: 'waiting',
      item: null,
      source,
      title: (task
        ? `Follow up: ${task.title}`
        : channel
          ? `Follow up in #${channel.name}`
          : `Follow up with ${peer!.name}`
      ).slice(0, 240),
      counterparty: peer?.name || '',
      projectId: task?.projectId || null,
    });
  }
  async function openFollowUpSource(source: FollowUpSource) {
    const generation = accountGeneration.current;
    const serial = ++followUpOpenSerial.current;
    const revision = dataRevision.current;
    const readRevision = canonicalReadRevision.current;
    const isCurrent = () => {
      if (generation !== accountGeneration.current || serial !== followUpOpenSerial.current)
        return false;
      if (revision !== dataRevision.current || readRevision !== canonicalReadRevision.current)
        throw new Error(
          'The workspace changed. Open the linked source again to use its latest details.',
        );
      return true;
    };
    try {
      if (source.kind === 'task') {
        const response = await api<{ tasks: Task[] }>('/tasks');
        if (!isCurrent()) return;
        const task = response.tasks.find(
          (task) => task.id === source.id && !deletedTaskIds.current.has(task.id),
        );
        if (!task)
          throw new Error(
            'The linked task is no longer available. You can unlink it in follow-up details.',
          );
        cacheTaskAcks([task]);
        changePersonalEditor(null);
        changeTaskEditor(withoutDeletedProject(task));
      } else if (source.kind === 'channel') {
        const response = await api<{ channels: Channel[] }>('/channels');
        if (!isCurrent()) return;
        const channel = response.channels.find((channel) => channel.id === source.id);
        if (!channel)
          throw new Error(
            'The linked channel is no longer available. You can unlink it in follow-up details.',
          );
        setChannels(response.channels);
        changePersonalEditor(null);
        navigate(`/channels/${channel.id}`);
      } else {
        const response = await api<{ workspace: Workspace; users: User[]; historyUsers: User[] }>(
          '/workspace',
        );
        if (!isCurrent()) return;
        canonicalReadRevision.current++;
        cacheRoster(response.users, response.historyUsers);
        const peer = response.users.find((peer) => peer.id === source.id);
        if (!peer)
          throw new Error(
            'The linked person is no longer available. You can still resolve, snooze, or unlink this follow-up.',
          );
        changePersonalEditor(null);
        navigate(`/people/${peer.id}`);
      }
    } catch (error) {
      if (generation === accountGeneration.current && serial === followUpOpenSerial.current)
        setToast({ text: errorMessage(error), error: true });
    }
  }
  async function createPersonalItem(payload: NewPersonalItem) {
    const generation = accountGeneration.current;
    const { item } = await api<{ item: PersonalItem }>('/items', 'POST', payload);
    if (generation !== accountGeneration.current) return;
    dataRevision.current++;
    cachePersonalItem(item);
    if (personalItemNeedsReconciliation(item)) reconcilePersonalItems();
    if (generation === accountGeneration.current)
      notify(`Private ${ITEM_LABEL[item.kind].toLowerCase()} created.`);
  }
  async function updatePersonalItem(id: string, patch: PersonalItemPatch) {
    const generation = accountGeneration.current;
    try {
      const response = await api<{
        item: PersonalItem;
        nextItem?: PersonalItem | null;
        notice?: string;
      }>(`/items/${id}`, 'PATCH', patch);
      if (generation !== accountGeneration.current) return;
      dataRevision.current++;
      const saved = [response.item, ...(response.nextItem ? [response.nextItem] : [])];
      saved.forEach(cachePersonalItem);
      if (saved.some(personalItemNeedsReconciliation)) reconcilePersonalItems();
      notify(
        response.notice ||
          (response.nextItem
            ? 'Reminder completed. Next occurrence created.'
            : response.item.kind === 'waiting' && patch.completed !== undefined
              ? patch.completed
                ? 'Follow-up resolved.'
                : 'Follow-up reopened.'
              : `Private ${ITEM_LABEL[response.item.kind].toLowerCase()} updated.`),
      );
    } catch (error) {
      if (
        generation === accountGeneration.current &&
        error instanceof APIError &&
        error.status === 409
      )
        await refreshPrivateItems().catch(() => {});
      throw error;
    }
  }
  async function configureItemRecurrence(
    id: string,
    recurrence: RecurrenceConfig | null,
    expectedVersion: number,
  ): Promise<PersonalItem> {
    const generation = accountGeneration.current;
    try {
      const { item, notice } = await api<{ item: PersonalItem; notice?: string }>(
        `/items/${id}/recurrence`,
        'PATCH',
        { expectedVersion, recurrence },
      );
      if (generation !== accountGeneration.current) throw new Error('Your account changed.');
      dataRevision.current++;
      cachePersonalItem(item);
      if (personalItemNeedsReconciliation(item)) reconcilePersonalItems();
      notify(
        notice ||
          (recurrence
            ? 'Reminder repeat updated.'
            : 'Future repeats stopped. The current reminder is kept.'),
      );
      return safePersonalItem(item);
    } catch (error) {
      if (
        generation === accountGeneration.current &&
        error instanceof APIError &&
        error.status === 409
      )
        await refreshPrivateItems().catch(() => {});
      throw error;
    }
  }
  async function removePersonalItem() {
    if (!personalDelete || personalDeleteRunning.current) return;
    const item = personalDelete;
    const generation = accountGeneration.current;
    personalDeleteRunning.current = true;
    setPersonalDeleting(true);
    setPersonalDeleteError('');
    try {
      await api(`/items/${item.id}`, 'DELETE', { expectedVersion: item.version });
      if (generation !== accountGeneration.current) return;
      dataRevision.current++;
      deletedItemIds.current.add(item.id);
      setItems((previous) => previous.filter((existing) => existing.id !== item.id));
      setPersonalDelete(null);
      changePersonalEditor((previous) => (previous?.item?.id === item.id ? null : previous));
      if (item.recurrence) {
        rememberDeletedRule(item.recurrence);
        setItems((previous) => previous.map((existing) => safePersonalItem(existing, false)));
        reconcilePersonalItems();
      }
      notify(
        item.recurrence?.active
          ? 'Current reminder deleted and future repeats stopped.'
          : 'Private item deleted.',
      );
    } catch (error) {
      if (generation === accountGeneration.current) {
        if (error instanceof APIError && error.status === 409)
          await refreshPrivateItems().catch(() => {});
        setPersonalDeleteError(
          `${errorMessage(error)} Close this dialog and review the latest item before trying again.`,
        );
      }
    } finally {
      if (generation === accountGeneration.current) {
        personalDeleteRunning.current = false;
        setPersonalDeleting(false);
      }
    }
  }
  async function searchWorkspace(query: string) {
    return api<SearchResults>(`/search?q=${encodeURIComponent(query)}`);
  }
  async function loadUpcoming(days: 7 | 30, projectId: string | null, isCurrent: () => boolean) {
    const generation = accountGeneration.current;
    const revision = dataRevision.current;
    const readRevision = canonicalReadRevision.current;
    const params = new URLSearchParams({ days: String(days), timeZone });
    if (projectId) params.set('projectId', projectId);
    const response = await api<UpcomingSnapshot>(`/upcoming?${params}`);
    if (generation !== accountGeneration.current) throw new Error('Your account changed.');
    if (
      !isCurrent() ||
      revision !== dataRevision.current ||
      readRevision !== canonicalReadRevision.current
    )
      throw new Error('The workspace changed. Refresh Upcoming to load the latest schedule.');
    const currentTasks = response.tasks
      .filter((task) => !deletedTaskIds.current.has(task.id))
      .map(withoutDeletedProject);
    const currentItems = response.items
      .filter((item) => !deletedItemIds.current.has(item.id))
      .map((item) => safePersonalItem(item));
    cacheTaskAcks(currentTasks);
    currentItems.forEach(cachePersonalItem);
    return { ...response, tasks: currentTasks, items: currentItems };
  }
  async function loadForgotten(
    days: 7 | 14 | 30,
    projectId: string | null,
    isCurrent: () => boolean,
  ): Promise<ForgottenSnapshot> {
    const generation = accountGeneration.current;
    const revision = dataRevision.current;
    const readRevision = canonicalReadRevision.current;
    const params = new URLSearchParams({ days: String(days) });
    if (projectId) params.set('projectId', projectId);
    const response = await api<ForgottenSnapshot>(`/forgotten?${params}`);
    if (generation !== accountGeneration.current) throw new Error('Your account changed.');
    if (
      !isCurrent() ||
      revision !== dataRevision.current ||
      readRevision !== canonicalReadRevision.current
    )
      throw new Error('The workspace changed. Refresh your review to load the latest tasks.');
    const current = response.tasks
      .filter((task) => !deletedTaskIds.current.has(task.id))
      .map(withoutDeletedProject);
    cacheTaskAcks(current);
    return { ...response, tasks: current };
  }
  async function dismissForgotten(task: Task): Promise<ForgottenReview> {
    const generation = accountGeneration.current;
    try {
      const response = await api<{ review: ForgottenReview }>(
        `/forgotten/${task.id}/dismiss`,
        'POST',
        { expectedVersion: task.version },
      );
      if (generation !== accountGeneration.current) throw new Error('Your account changed.');
      dataRevision.current++;
      return response.review;
    } catch (error) {
      if (
        generation === accountGeneration.current &&
        error instanceof APIError &&
        error.status === 409
      )
        await refreshTaskCache().catch(() => {});
      throw error;
    }
  }
  function withoutDeletedProject(task: Task): Task {
    // A task response can arrive after a newer project deletion acknowledged.
    return normalizeRecurrence(
      task.projectId && deletedProjectIds.current.has(task.projectId)
        ? { ...task, projectId: null }
        : task,
    );
  }
  function requestDelete(task: Task) {
    setDeleteError('');
    setDeleteRequest({ kind: 'task', task });
  }
  function requestProjectDelete(project: Project) {
    setDeleteError('');
    setDeleteRequest({ kind: 'project', project });
  }
  async function confirmDelete() {
    if (!deleteRequest || deletionRunning.current) return;
    const request = deleteRequest;
    const generation = accountGeneration.current;
    deletionRunning.current = true;
    setDeleting(true);
    setDeleteError('');
    try {
      const isProject = request.kind === 'project';
      const id = isProject ? request.project.id : request.task.id;
      await api(
        `/${isProject ? 'projects' : 'tasks'}/${id}`,
        'DELETE',
        isProject ? {} : { expectedVersion: request.task.version },
      );
      if (generation !== accountGeneration.current) return;
      dataRevision.current++;
      if (isProject) {
        deletedProjectIds.current.add(id);
        setProjects((previous) => previous.filter((project) => project.id !== id));
        setItems((previous) =>
          previous.map((item) => (item.projectId === id ? { ...item, projectId: null } : item)),
        );
        reconcilePersonalItems();
        setTasks((previous) =>
          previous.map((task) => (task.projectId === id ? { ...task, projectId: null } : task)),
        );
        changeTaskEditor((previous) =>
          previous && previous !== 'new' && previous.projectId === id
            ? { ...previous, projectId: null }
            : previous,
        );
        setNewTaskDefaults((previous) =>
          previous.projectId === id ? { ...previous, projectId: null } : previous,
        );
        await reconcileTasks();
        if (generation !== accountGeneration.current) return;
        if (location.pathname === `/projects/${id}`) navigate('/projects');
        notify('Project deleted. Its tasks are still in Tasks, with no project.');
      } else {
        deletedTaskIds.current.add(id);
        taskCache.current = taskCache.current.filter((task) => task.id !== id);
        setTasks((previous) => previous.filter((task) => task.id !== id));
        setItems((previous) =>
          previous.map((item) =>
            item.source?.kind === 'task' && item.source.id === id
              ? { ...item, source: null }
              : item,
          ),
        );
        reconcilePersonalItems();
        if (editor !== 'new' && editor?.id === id) changeTaskEditor(null);
        if (request.kind === 'task' && request.task.recurrence) {
          rememberDeletedRule(request.task.recurrence);
          taskCache.current = taskCache.current.map((task) => normalizeRecurrence(task, false));
          setTasks((previous) => previous.map((task) => normalizeRecurrence(task, false)));
          void reconcileTasks();
        }
        notify(
          request.kind === 'task' && request.task.recurrence?.active
            ? 'Current task deleted and future repeats stopped.'
            : 'Task deleted.',
        );
      }
      request.resolve?.(true);
      setDeleteRequest(null);
    } catch (error) {
      if (generation === accountGeneration.current) {
        if (error instanceof APIError && error.status === 409 && request.kind === 'task')
          await refreshTaskCache().catch(() => {});
        setDeleteError(errorMessage(error));
      }
    } finally {
      if (generation === accountGeneration.current) {
        deletionRunning.current = false;
        setDeleting(false);
      }
    }
  }
  function cancelDelete() {
    if (deletionRunning.current) return;
    deleteRequest?.resolve?.(false);
    setDeleteRequest(null);
  }
  async function logout() {
    const generation = accountGeneration.current;
    const oldUserId = user?.id;
    try {
      await api('/auth/logout', 'POST');
      if (generation !== accountGeneration.current) return;
      if (oldUserId) clearBrowserBinding(oldUserId);
      resetAccount();
      if (path !== '/join') navigate('/');
    } catch (error) {
      if (generation === accountGeneration.current)
        setToast({ text: errorMessage(error), error: true });
    }
  }
  function insertCommand(value: string) {
    setCommand(value);
    requestAnimationFrame(() => {
      commandRef.current?.focus();
      commandRef.current?.setSelectionRange(value.length, value.length);
    });
  }
  function cancelAssistant() {
    assistantSerial.current++;
    assistantController.current?.abort();
    assistantController.current = null;
    setAssistant((previous) =>
      previous?.loading
        ? { ...previous, loading: false, error: 'Request cancelled. You can ask again.' }
        : previous,
    );
  }
  async function askAssistant(prompt: string, scope: AiScope) {
    assistantController.current?.abort();
    const controller = new AbortController();
    assistantController.current = controller;
    const serial = ++assistantSerial.current;
    const generation = accountGeneration.current;
    setAssistant({ id: serial, prompt, scope, loading: true });
    setAssistantOpen(true);
    try {
      const config = await api<AiConfiguration>('/ai/config', 'GET', undefined, controller.signal);
      if (generation !== accountGeneration.current || serial !== assistantSerial.current) return;
      if (!config.enabled)
        throw new Error(
          'The assistant is not configured for this workspace. Your normal commands are still available.',
        );
      const response = await api<AiAskResponse>(
        '/ai/ask',
        'POST',
        { prompt, scope, timeZone },
        controller.signal,
      );
      if (generation !== accountGeneration.current || serial !== assistantSerial.current) return;
      setAssistant({ id: serial, prompt, scope, loading: false, response });
    } catch (error) {
      if (generation === accountGeneration.current && serial === assistantSerial.current)
        setAssistant({ id: serial, prompt, scope, loading: false, error: errorMessage(error) });
    } finally {
      if (serial === assistantSerial.current) assistantController.current = null;
    }
  }
  async function applyAssistantProposal(proposal: AiProposal) {
    if (!assistantProposalContext(proposal).available)
      throw new Error(
        'This target or a referenced person or project is no longer available. Refresh and ask again.',
      );
    const generation = accountGeneration.current;
    const readRevision = canonicalReadRevision.current;
    try {
      const response = await api<AiConfirmResponse>(
        `/ai/proposals/${encodeURIComponent(proposal.id)}/confirm`,
        'POST',
        {},
      );
      if (generation !== accountGeneration.current) throw new Error('Your account changed.');
      dataRevision.current++;
      const result = response.result;
      if (response.replayed || readRevision !== canonicalReadRevision.current) {
        void (async () => {
          const pending = refreshRequest.current;
          if (pending?.generation === generation) await pending.promise.catch(() => {});
          if (generation === accountGeneration.current) await refresh();
        })().catch(() => {
          if (generation === accountGeneration.current)
            setError(
              'Your action was applied. Workspace data could not be refreshed; retry to load its current state.',
            );
        });
        setSearchRevision((previous) => previous + 1);
        return `${response.replayed ? 'Already applied.' : 'Applied.'} Current workspace data is being refreshed.`;
      }
      if ('task' in result) {
        const saved = [result.task, ...(result.nextTask ? [result.nextTask] : [])];
        cacheTaskAcks(saved);
        if (saved.some((task) => task.projectId && deletedProjectIds.current.has(task.projectId)))
          void refreshTaskCache().catch(() => {
            if (generation === accountGeneration.current)
              setTaskSyncError(
                'Your action was applied. Refresh tasks to load the latest project association.',
              );
          });
      } else if ('item' in result) {
        const saved = [result.item, ...(result.nextItem ? [result.nextItem] : [])];
        saved.forEach(cachePersonalItem);
        if (saved.some(personalItemNeedsReconciliation)) reconcilePersonalItems();
      } else if (!deletedProjectIds.current.has(result.project.id)) {
        setProjects((previous) => [
          result.project,
          ...previous.filter((project) => project.id !== result.project.id),
        ]);
      }
      setSearchRevision((previous) => previous + 1);
      return `${response.replayed ? 'Already applied.' : 'Applied.'}${'notice' in result && result.notice ? ` ${result.notice}` : ''}`;
    } catch (error) {
      if (
        generation === accountGeneration.current &&
        error instanceof APIError &&
        error.status === 409
      )
        void refresh().catch(() => {});
      throw error;
    }
  }
  function assistantProposalContext(proposal: AiProposal) {
    const action = proposal.action;
    const payload = action.payload;
    let available = true;
    const labels: string[] = [];
    let target = 'New shared project';
    if (action.type === 'task.update') {
      const task = tasks.find((task) => task.id === action.id);
      target = task?.title || 'Task unavailable';
      available = !!task;
    } else if (action.type === 'item.update') {
      const item = items.find((item) => item.id === action.id);
      target = item?.title || 'Private item unavailable';
      available = !!item;
    } else if ('title' in payload) target = payload.title || 'New item';
    else if ('name' in payload) target = payload.name;
    if ('projectId' in payload && payload.projectId) {
      const project = projects.find((project) => project.id === payload.projectId);
      labels.push(`Project: ${project?.name || 'unavailable'} (${payload.projectId})`);
      available = available && !!project;
    }
    if ('assigneeId' in payload && payload.assigneeId) {
      const member = users.find((member) => member.id === payload.assigneeId);
      labels.push(`Assignee: ${member?.name || 'unavailable'} (${payload.assigneeId})`);
      available = available && !!member;
    }
    if ('source' in payload && payload.source) {
      const source = payload.source;
      const label =
        source.kind === 'task'
          ? tasks.find((task) => task.id === source.id)?.title
          : source.kind === 'channel'
            ? channels.find((channel) => channel.id === source.id)?.name
            : users.find((member) => member.id === source.id)?.name;
      labels.push(`Follow-up source: ${label || 'unavailable'} (${source.id})`);
      available = available && !!label;
    }
    return { target, labels, available };
  }
  function assistantSourceLabel(source: AiSource) {
    if (source.kind === 'task') return tasks.find((task) => task.id === source.id)?.title || null;
    if (source.kind === 'project')
      return projects.find((project) => project.id === source.id)?.name || null;
    return items.find((item) => item.id === source.id)?.title || null;
  }
  function openAssistantSource(source: AiSource) {
    if (!assistantSourceLabel(source)) return;
    setAssistantOpen(false);
    if (source.kind === 'project') navigate(`/projects/${source.id}`);
    else if (source.kind === 'task')
      changeTaskEditor(taskCache.current.find((task) => task.id === source.id) || null);
    else {
      const item = items.find((item) => item.id === source.id);
      if (item) editPersonalItem(item);
    }
  }
  function assistantDraftLabel(draft: AiMessageDraft) {
    if (!!draft.channelId === !!draft.recipientId) return null;
    return draft.channelId
      ? (channels.find((channel) => channel.id === draft.channelId)?.name &&
          `#${channels.find((channel) => channel.id === draft.channelId)!.name}`) ||
          null
      : users.find((member) => member.id === draft.recipientId)?.name || null;
  }
  function openAssistantDraft(draft: AiMessageDraft) {
    if (!assistantDraftLabel(draft)) return;
    setAssistantOpen(false);
    setMessageDraft({ ...draft, nonce: Date.now() });
    navigate(draft.channelId ? `/channels/${draft.channelId}` : `/people/${draft.recipientId}`);
  }
  async function runCommand(input: string) {
    if (commandRunning.current)
      throw new Error('Another command is running. Wait for it to finish and try again.');
    const generation = accountGeneration.current;
    commandRunning.current = true;
    setCommandBusy(true);
    setHistory((previous) => [...previous, input].slice(-100));
    let output = '';
    try {
      const action = parseCommand(input, context);
      switch (action.type) {
        case 'ai.ask':
          void askAssistant(action.prompt, action.scope);
          output = 'Assistant review opened.';
          break;
        case 'clear':
          setEntries([]);
          setCommand('');
          return;
        case 'help':
          output = COMMANDS.map((item) => `${item.usage}\n  ${item.description}`).join('\n\n');
          break;
        case 'navigate': {
          navigate(action.path);
          const destination = new URL(action.path, location.origin);
          const route = destination.pathname;
          const label =
            route === '/forgotten'
              ? 'Forgotten tasks'
              : route === '/upcoming'
                ? 'Upcoming'
                : route === '/settings' && destination.hash === '#notifications'
                  ? 'Notifications'
                  : route === '/projects/new'
                    ? 'project setup'
                    : route === '/profile'
                      ? 'your profile'
                      : route.startsWith('/profiles/')
                        ? `profile for ${users.find((member) => route.endsWith(member.id))?.name || 'teammate'}`
                        : route.startsWith('/projects/')
                          ? projects.find((project) => route.endsWith(project.id))?.name ||
                            'project'
                          : route.startsWith('/channels/')
                            ? `#${channels.find((channel) => route.endsWith(channel.id))?.name || 'channel'}`
                            : route.startsWith('/people/')
                              ? users.find((member) => route.endsWith(member.id))?.name ||
                                'conversation'
                              : route.slice(1) || 'workspace';
          output = `Opened ${label}.`;
          break;
        }
        case 'task.new': {
          const project = projects.find((item) => path === `/projects/${item.id}`);
          if (!project) navigate('/tasks');
          openNewTask(project?.id || null);
          output = 'Opened Create task.';
          break;
        }
        case 'task.create':
          await createTask(action.payload);
          output = `Created task: ${action.payload.title}`;
          break;
        case 'task.update':
          await updateTask(action.id, action.patch);
          output = `${action.patch.status ? STATUS_LABEL[action.patch.status] : 'Updated'}: ${tasks.find((task) => task.id === action.id)?.title || action.id}`;
          break;
        case 'task.delete': {
          const task = tasks.find((task) => task.id === action.id)!;
          const confirmed = await new Promise<boolean>((resolve) => {
            setDeleteError('');
            setDeleteRequest({ kind: 'task', task, resolve });
          });
          output = confirmed
            ? `Deleted task: ${task.title}`
            : 'Deletion cancelled. Your task is still here.';
          break;
        }
        case 'project.delete': {
          const project = projects.find((project) => project.id === action.id)!;
          const confirmed = await new Promise<boolean>((resolve) => {
            setDeleteError('');
            setDeleteRequest({ kind: 'project', project, resolve });
          });
          output = confirmed
            ? `Deleted project: ${project.name}. Its tasks remain in the workspace with no project.`
            : 'Deletion cancelled. Your project is still here.';
          break;
        }
        case 'task.list': {
          const revision = dataRevision.current;
          const data = await api<{ tasks: Task[] }>('/tasks');
          if (generation !== accountGeneration.current) return;
          const listed = data.tasks
            .filter((task) => !deletedTaskIds.current.has(task.id))
            .map(withoutDeletedProject)
            .map((task) => normalizeRecurrence(task, false));
          if (revision === dataRevision.current) {
            canonicalReadRevision.current++;
            taskCache.current = listed;
            setTasks(listed);
          }
          output = listed.length
            ? listed
                .map(
                  (task) =>
                    `${task.id.slice(0, 8)}  ${STATUS_LABEL[task.status].padEnd(11)}  ${task.title}`,
                )
                .join('\n')
            : 'No tasks yet. Create one with: task add Your next thing';
          break;
        }
        case 'item.create':
          await createPersonalItem(action.payload);
          output = `Private ${ITEM_LABEL[action.payload.kind].toLowerCase()} created: ${action.payload.title}`;
          break;
        case 'item.update':
          await updatePersonalItem(action.id, action.patch);
          output = 'Private item updated.';
          break;
        case 'search':
          setSearchQuery(action.query);
          setSearchRun((previous) => previous + 1);
          navigate('/search');
          output = 'Opened your search results.';
          break;
        case 'agenda.status': {
          const agenda = await api<Agenda>(`/agenda?timeZone=${encodeURIComponent(timeZone)}`);
          if (generation !== accountGeneration.current) return;
          const due = agenda.items.filter(
            (item) => item.scheduledAt && Date.parse(item.scheduledAt) <= Date.parse(agenda.now),
          );
          const orderedTasks = [...agenda.tasks].sort(
            (a, b) =>
              (a.dueDate || 'z').localeCompare(b.dueDate || 'z') ||
              a.createdAt.localeCompare(b.createdAt) ||
              a.id.localeCompare(b.id),
          );
          const orderedItems = (list: PersonalItem[]) =>
            [...list].sort(
              (a, b) =>
                (a.scheduledAt || 'z').localeCompare(b.scheduledAt || 'z') ||
                a.createdAt.localeCompare(b.createdAt) ||
                a.id.localeCompare(b.id),
            );
          const section = (label: string, lines: string[]) =>
            `${label} (${lines.length})${
              lines.length
                ? '\n' +
                  lines
                    .slice(0, 10)
                    .map((line) => `  - ${line}`)
                    .join('\n')
                : ''
            }${lines.length > 10 ? `\n  +${lines.length - 10} more` : ''}`;
          output = [
            `Today · ${agenda.date} · ${agenda.timeZone}`,
            `${agenda.tasks.length} unfinished tasks in your agenda. Private items below are only yours.`,
            section(
              'Overdue tasks',
              orderedTasks
                .filter((task) => task.dueDate && task.dueDate < agenda.date)
                .map((task) => `${task.title} · due ${task.dueDate}`),
            ),
            section(
              'Tasks due today',
              orderedTasks.filter((task) => task.dueDate === agenda.date).map((task) => task.title),
            ),
            section(
              'Private reminders due',
              orderedItems(due.filter((item) => item.kind === 'reminder')).map(
                (item) => item.title,
              ),
            ),
            section(
              'Private follow-ups due',
              orderedItems(due.filter((item) => item.kind === 'waiting')).map(
                (item) =>
                  `${item.title}${item.counterparty ? ` · waiting for ${item.counterparty}` : ''}`,
              ),
            ),
            section(
              'Waiting',
              orderedItems(agenda.items.filter((item) => item.kind === 'waiting')).map(
                (item) =>
                  `${item.title}${item.counterparty ? ` · waiting for ${item.counterparty}` : ''}`,
              ),
            ),
          ].join('\n\n');
          navigate('/terminal');
          break;
        }
        case 'atomic.command': {
          const { data } = await api<{ data: unknown }>('/atomic/command', 'POST', {
            operation: `read.${action.command}`,
            input: action.days === undefined ? {} : { days: action.days },
          });
          output = atomicResultText(data);
          break;
        }
        case 'atomic.draft': {
          const operationId = crypto.randomUUID();
          const result = await api<{
            data: {
              draftId?: string;
              reviewStatus?: string;
              jobId?: string;
              model?: string;
              decisionReview?: string;
            };
          }>('/atomic/command', 'POST', {
            operation: action.mode === 'post' ? 'draft.post' : 'draft.brief',
            input:
              action.mode === 'post'
                ? { operationId, text: action.text }
                : { operationId, prompt: action.text },
          });
          output =
            action.mode === 'post'
              ? `Created Atomic draft ${result.data.draftId || '(ID unavailable)'}. Decisions review: ${result.data.reviewStatus || 'not reported'}. Atomic approval is still required; nothing was published.`
              : `Queued Atomic draft generation ${result.data.jobId || '(job ID unavailable)'} using ${result.data.model || 'the configured model'}. ${result.data.decisionReview || ''} Review and approve in Atomic; nothing was published.`;
          break;
        }
        case 'recurrence.update':
          if (action.kind === 'task')
            await configureTaskRecurrence(action.id, action.recurrence, action.expectedVersion);
          else await configureItemRecurrence(action.id, action.recurrence, action.expectedVersion);
          output = 'Future repeats stopped. The current occurrence is kept.';
          break;
        case 'message.send': {
          await api<{ message: Message }>('/messages', 'POST', action.payload);
          output = `Message sent to ${action.payload.channelId ? `#${channels.find((channel) => channel.id === action.payload.channelId)?.name}` : users.find((member) => member.id === action.payload.recipientId)?.name || 'your teammate'}.`;
          break;
        }
        case 'channel.create': {
          const { channel } = await api<{ channel: Channel }>('/channels', 'POST', action.payload);
          if (generation !== accountGeneration.current) return;
          dataRevision.current++;
          setChannels((previous) => [
            ...previous.filter((existing) => existing.id !== channel.id),
            channel,
          ]);
          output = `Created #${channel.name}.`;
          break;
        }
      }
      if (generation !== accountGeneration.current) return;
      setEntries((previous) => [
        ...previous,
        {
          id: ++entryId.current,
          command: input,
          result: output,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
      setCommand('');
      if (
        action.type !== 'task.create' &&
        action.type !== 'task.update' &&
        action.type !== 'task.delete' &&
        action.type !== 'item.create' &&
        action.type !== 'item.update'
      )
        notify(
          action.type === 'atomic.command' || action.type === 'atomic.draft'
            ? 'Atomic result added to Terminal.'
            : action.type === 'help' || action.type === 'task.list'
              ? 'Result added to Terminal.'
              : output,
        );
      if (action.type === 'help' || action.type === 'task.list') navigate('/terminal');
    } catch (error) {
      if (generation !== accountGeneration.current) throw error;
      const message = errorMessage(error);
      if (error instanceof APIError && error.status === 0) setError(message);
      setEntries((previous) => [
        ...previous,
        {
          id: ++entryId.current,
          command: input,
          result: message,
          error: true,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
      setToast({ text: message, error: true });
      throw error;
    } finally {
      if (generation === accountGeneration.current) {
        commandRunning.current = false;
        setCommandBusy(false);
      }
    }
  }
  if (!sessionReady)
    return (
      <div className="initial-loading">
        <div className="brand">
          <BrandIdentity />
        </div>
        {sessionError ? (
          <ErrorNotice message={sessionError} retry={() => void loadSession()} />
        ) : (
          <p role="status">
            Opening your workspace<span className="loading-dots">…</span>
          </p>
        )}
      </div>
    );
  if (!user)
    return (
      <>
        <Suspense
          fallback={
            <div className="initial-loading" role="status">
              Loading sign-in…
            </div>
          }
        >
          <Auth
            needsSetup={needsSetup}
            bootstrapRequired={bootstrapRequired}
            onLogin={(account, isNewAccount) => {
              resetAccount();
              setUser(account);
              setAuthMethod('password');
              setNeedsSetup(false);
              setLoading(true);
              navigate(isNewAccount ? '/profile?welcome=1' : '/tasks');
              void loadSession();
            }}
          />
        </Suspense>
        {toast && (
          <div className={`toast ${toast.error ? 'toast-error' : ''}`} role="status">
            {toast.text}
          </div>
        )}
      </>
    );
  if (path === '/join')
    return (
      <div className="initial-loading">
        <div className="brand">
          <BrandIdentity />
        </div>
        <h1>You’re already signed in.</h1>
        <p>You’re signed in as {user.email}. Sign out to use this invitation for a new account.</p>
        <div className="join-actions">
          <Button color="secondary" variant="outline" onClick={() => navigate('/tasks')}>
            Go to workspace
          </Button>
          <Button color="primary" onClick={() => void logout()}>
            Sign out to use invitation
          </Button>
        </div>
        {toast && <ErrorNotice message={toast.text} />}
      </div>
    );
  const activeChannel = path.startsWith('/channels/')
    ? channels.find((channel) => channel.id === path.split('/')[2])
    : undefined;
  const activePeer = path.startsWith('/people/')
    ? [...users, ...historyUsers].find((member) => member.id === path.split('/')[2])
    : undefined;
  const displayUsers = [...users, ...historyUsers];
  const tasksPage = path === '/' || path === '/tasks';
  const profileId =
    path === '/profile'
      ? user.id
      : path.startsWith('/profiles/')
        ? path.split('/')[2].toLowerCase() === 'me'
          ? user.id
          : path.split('/')[2].toLowerCase()
        : null;
  const personalTab = ['/today', '/morning', '/reminders', '/waiting'].includes(path)
    ? path === '/reminders'
      ? 'reminders'
      : path === '/waiting'
        ? 'waiting'
        : 'today'
    : null;
  const dueCount = items.filter(
    (item) =>
      (item.kind === 'reminder' || item.kind === 'waiting') &&
      !item.completedAt &&
      item.scheduledAt &&
      Date.parse(item.scheduledAt) <= clock,
  ).length;
  const newProjectPage = path === '/projects/new' || path === '/new-project';
  const notificationGeneration = accountGeneration.current;
  const activeProject = projects.find((project) => path === `/projects/${project.id}`);
  const pageLabel = path.startsWith('/crm')
    ? crmPageLabel(path)
    : personalTab
      ? personalTab === 'today'
        ? 'Today'
        : personalTab === 'reminders'
          ? 'Reminders'
          : 'Waiting'
      : path === '/admin'
        ? 'Accounts'
        : path === '/upcoming'
          ? 'Upcoming'
          : path === '/forgotten'
            ? 'Forgotten tasks'
            : profileId
              ? profileId === user.id
                ? 'Your profile'
                : 'Profile'
              : path === '/notes'
                ? 'Notes'
                : path === '/bookmarks'
                  ? 'Bookmarks'
                  : path === '/search'
                    ? 'Search'
                    : tasksPage
                      ? 'Tasks'
                      : newProjectPage
                        ? 'New project'
                        : activeProject
                          ? activeProject.name
                          : path === '/projects'
                            ? 'Projects'
                            : path === '/terminal'
                              ? 'Terminal'
                              : path === '/shortcuts'
                                ? 'Shortcuts'
                                : path === '/settings'
                                  ? 'Workspace settings'
                                  : path === '/people'
                                    ? 'People'
                                    : activeChannel
                                      ? `# ${activeChannel.name}`
                                      : activePeer
                                        ? activePeer.name
                                        : 'Workspace';
  const navItem = (route: string, label: string, icon: React.ReactNode, count?: number) => (
    <a
      href={route}
      className={`nav-item ${path === route || (route === '/tasks' && path === '/') || (route === '/today' && ['/today', '/morning', '/upcoming', '/forgotten', '/reminders', '/waiting', '/notes', '/bookmarks', '/search'].includes(path)) || (route === '/projects' && (newProjectPage || activeProject)) || (route === '/crm' && path.startsWith('/crm')) ? 'active' : ''}`}
      onClick={(event) => {
        event.preventDefault();
        navigate(route);
      }}
    >
      {icon}
      <span>{label}</span>
      {count !== undefined && count > 0 && <small>{count}</small>}
      {route === '/terminal' && <Kbd>⌘ K</Kbd>}
    </a>
  );
  return (
    <div className={`app-shell ${mobileNav ? 'nav-open' : ''}`}>
      {mobileNav && (
        <button
          className="nav-scrim"
          aria-label="Close navigation"
          onClick={() => setMobileNav(false)}
        />
      )}
      <aside className="sidebar">
        <a
          className="brand sidebar-brand"
          href="/tasks"
          onClick={(event) => {
            event.preventDefault();
            navigate('/tasks');
          }}
        >
          <BrandIdentity />
        </a>
        <button className="workspace-switch" onClick={() => navigate('/settings')}>
          <span className="workspace-avatar">
            {workspace?.name.slice(0, 1).toUpperCase() || 'W'}
          </span>
          <span>
            <strong>{workspace?.name || 'Your workspace'}</strong>
            <small>Workspace</small>
          </span>
          <SettingsIcon />
        </button>
        <nav className="primary-nav" aria-label="Main navigation">
          {navItem('/today', 'Today', <CalendarToday />)}
          {navItem(
            '/tasks',
            'Tasks',
            <NotebookCheck />,
            tasks.filter((task) => task.status !== 'done').length,
          )}
          {navItem('/terminal', 'Terminal', <TerminalIcon />)}
          {navItem('/projects', 'Projects', <NotebookCheck />, projects.length)}
          {navItem('/shortcuts', 'Shortcuts', <Keyboard />)}
          {user.canAdmin && navItem('/crm', 'CRM', <Bills />)}
        </nav>
        <div className="sidebar-divider" />
        <div className="sidebar-section">
          <div className="sidebar-section-heading">
            <button onClick={() => navigate('/projects')}>Projects</button>
            <Button
              color="secondary"
              variant="ghost"
              size="xs"
              uniform
              onClick={() => navigate('/projects/new')}
              aria-label="Create project"
            >
              <Plus />
            </Button>
          </div>
          <nav aria-label="Projects">
            {projects.map((project) => (
              <a
                key={project.id}
                href={`/projects/${project.id}`}
                className={`nav-item ${activeProject?.id === project.id ? 'active' : ''}`}
                onClick={(event) => {
                  event.preventDefault();
                  navigate(`/projects/${project.id}`);
                }}
              >
                <NotebookCheck />
                <span>{project.name}</span>
              </a>
            ))}
          </nav>
          {!projects.length && !loading && (
            <p className="sidebar-empty">Start with /new-project.</p>
          )}
        </div>
        <div className="sidebar-section">
          <div className="sidebar-section-heading">
            <h2>Channels</h2>
            <Button
              color="secondary"
              variant="ghost"
              size="xs"
              uniform
              onClick={() => setChannelModal(true)}
              aria-label="Create channel"
            >
              <Plus />
            </Button>
          </div>
          <nav aria-label="Channels">
            {channels.map((channel) => (
              <a
                key={channel.id}
                href={`/channels/${channel.id}`}
                onClick={(event) => {
                  event.preventDefault();
                  navigate(`/channels/${channel.id}`);
                }}
                className={`nav-item channel-link ${activeChannel?.id === channel.id ? 'active' : ''}`}
              >
                <span className="hash">#</span>
                <span>{channel.name}</span>
                {!!unread.channels[channel.id] && (
                  <small
                    className="unread-count"
                    aria-label={`${unread.channels[channel.id]} recent unread messages`}
                  >
                    {unread.channels[channel.id]}
                  </small>
                )}
              </a>
            ))}
          </nav>
          {!channels.length && !loading && (
            <p className="sidebar-empty">Create a channel to get talking.</p>
          )}
        </div>
        <div className="sidebar-section people-nav">
          <div className="sidebar-section-heading">
            <button onClick={() => navigate('/people')}>Direct messages</button>
            <Button
              color="secondary"
              variant="ghost"
              size="xs"
              uniform
              onClick={() => navigate('/people')}
              aria-label="Find a person"
            >
              <Plus />
            </Button>
          </div>
          <nav aria-label="Direct messages">
            {users.map((member) => (
              <a
                key={member.id}
                href={`/people/${member.id}`}
                onClick={(event) => {
                  event.preventDefault();
                  navigate(`/people/${member.id}`);
                }}
                className={`nav-item person-link ${activePeer?.id === member.id ? 'active' : ''}`}
              >
                <Avatar name={member.name} size={21} />
                <span>
                  {member.name}
                  {member.id === user.id && <small className="self-label"> (you)</small>}
                </span>
                {!!unread.people[member.id] && (
                  <span
                    className="message-bell"
                    key={bellRings[member.id] || 0}
                    data-ringing={bellRings[member.id] ? true : undefined}
                    title="New message"
                    aria-hidden="true"
                  >
                    <Bell />
                  </span>
                )}
                {!!unread.people[member.id] && (
                  <small
                    className="unread-count"
                    aria-label={`${unread.people[member.id]} recent unread messages`}
                  >
                    {unread.people[member.id]}
                  </small>
                )}
              </a>
            ))}
          </nav>
          {unreadError && (
            <p className="sidebar-empty" role="status">
              Recent unread counts unavailable.
            </p>
          )}
        </div>
        <div className="sidebar-bottom">
          {user.canAdmin && (
            <button className="invite-nav" onClick={() => navigate('/settings')}>
              <Members />
              <span>Invite teammates</span>
              <Plus />
            </button>
          )}
          <button
            className="account-nav"
            aria-label="Open your profile"
            onClick={() => navigate('/profile')}
          >
            <Avatar name={user.name} size={31} />
            <span>
              <strong>{user.name}</strong>
              <small>{user.canAdmin ? 'Admin' : 'Member'}</small>
            </span>
            <ArrowRight />
          </button>
          <div className="sidebar-signature">
            PANAMORPHIX<span>INTELLIGENCE</span>
          </div>
        </div>
      </aside>
      <div className="main-shell">
        <header className="topbar">
          <div className="topbar-location">
            <Button
              color="secondary"
              variant="ghost"
              uniform
              size="sm"
              className="mobile-menu"
              aria-label="Open navigation"
              onClick={() => setMobileNav(true)}
            >
              <Menu />
              {Object.values(unread.people).some(Boolean) && (
                <span
                  className="menu-bell message-bell"
                  key={Object.values(bellRings).reduce((sum, count) => sum + count, 0)}
                  data-ringing={Object.keys(bellRings).length > 0 || undefined}
                  aria-hidden="true"
                >
                  <Bell />
                </span>
              )}
            </Button>
            <span className="topbar-workspace">{workspace?.name || 'Workspace'}</span>
            <span className="breadcrumb-slash">/</span>
            <strong>{pageLabel}</strong>
          </div>
          {visible && dueCount > 0 && (
            <button
              className="global-due-indicator"
              onClick={() => navigate('/today')}
              aria-live="polite"
            >
              <Bell />
              <span>
                {dueCount} private item{dueCount === 1 ? '' : 's'} due
              </span>
            </button>
          )}
          <button
            className="quick-command"
            aria-label="Focus terminal command"
            onClick={() => commandRef.current?.focus()}
          >
            <TerminalIcon />
            <span>Command your workspace</span>
            <Kbd>⌘ K</Kbd>
          </button>
          <ThemeToggle />
          <Button
            className="workspace-settings-button"
            color="secondary"
            variant="ghost"
            uniform
            size="sm"
            aria-label="Open workspace settings"
            onClick={() => navigate('/settings')}
          >
            <SettingsIcon />
          </Button>
        </header>
        {error && (
          <div className="global-error">
            <ErrorNotice
              message={error}
              retry={() => {
                void refresh().catch((error) => setError(errorMessage(error)));
              }}
            />
          </div>
        )}
        {taskSyncError && (
          <div className="global-error">
            <ErrorNotice message={taskSyncError} retry={() => void reconcileTasks()} />
          </div>
        )}
        {personalSyncError && (
          <div className="global-error">
            <ErrorNotice message={personalSyncError} retry={reconcilePersonalItems} />
          </div>
        )}
        <main
          id="main-content"
          className={`main-content ${activeChannel || activePeer ? 'conversation-main' : ''}`}
        >
          {loading && !workspace ? (
            <div className="loading-content" role="status">
              Loading workspace…
            </div>
          ) : !workspace ? (
            <Empty
              icon={<TerminalIcon />}
              title="Workspace unavailable"
              description="Reconnect to load your tasks and conversations."
            >
              <Button
                color="primary"
                onClick={() => void refresh().catch((error) => setError(errorMessage(error)))}
              >
                Try again
              </Button>
            </Empty>
          ) : path === '/crm' || path.startsWith('/crm/') ? (
            <CrmApp
              path={path}
              query={routeQuery}
              navigate={navigate}
              userName={user.name}
              canAdmin={user.canAdmin}
            />
          ) : path === '/admin' ? (
            <AdminPage
              key={user.id}
              userId={user.id}
              canAdmin={user.canAdmin}
              isCurrentAccount={() => accountGeneration.current === notificationGeneration}
              onAccountChange={reconcileAdminChange}
            />
          ) : profileId ? (
            <ProfilePage
              key={`${user.id}-${profileId}`}
              profileId={profileId}
              userId={user.id}
              authMethod={authMethod}
              googleLinked={googleLinked}
              isCurrentAccount={() => accountGeneration.current === notificationGeneration}
              latest={profileId === user.id ? user : users.find((row) => row.id === profileId)}
              revision={dataRevision.current + canonicalReadRevision.current}
              welcome={
                profileId === user.id && new URLSearchParams(routeQuery).get('welcome') === '1'
              }
              deviceTimeZone={deviceTimeZone}
              load={loadProfile}
              save={saveProfile}
              navigate={navigate}
            />
          ) : personalTab ? (
            <TodayPage
              tasks={tasks}
              items={items}
              user={user}
              projects={projects}
              timeZone={timeZone}
              now={clock}
              tab={personalTab}
              navigate={navigate}
              onNewItem={(kind) => changePersonalEditor({ kind, item: null })}
              onEditItem={editPersonalItem}
              onCompleteItem={(item, completed) =>
                updatePersonalItem(item.id, { expectedVersion: item.version, completed })
              }
              onSnoozeItem={(item) =>
                updatePersonalItem(item.id, {
                  expectedVersion: item.version,
                  scheduledAt: new Date(Date.now() + 30 * 60000).toISOString(),
                })
              }
              onEditTask={changeTaskEditor}
              onCompleteTask={(task) => updateTask(task.id, { status: 'done' })}
              channels={channels}
              users={users}
              onOpenSource={(source) => void openFollowUpSource(source)}
            />
          ) : path === '/upcoming' ? (
            <UpcomingPage
              key={user.id}
              query={routeQuery}
              timeZone={timeZone}
              tasks={tasks}
              items={items}
              projects={projects}
              users={displayUsers}
              revision={dataRevision.current + canonicalReadRevision.current}
              visible={visible}
              load={loadUpcoming}
              navigate={navigate}
              onEditTask={changeTaskEditor}
              onCompleteTask={(task) =>
                updateTask(task.id, { status: 'done', expectedVersion: task.version })
              }
              onEditItem={editPersonalItem}
              onCompleteItem={(item) =>
                updatePersonalItem(item.id, { expectedVersion: item.version, completed: true })
              }
              onSnoozeItem={(item) =>
                updatePersonalItem(item.id, {
                  expectedVersion: item.version,
                  scheduledAt: new Date(Date.now() + 30 * 60000).toISOString(),
                })
              }
            />
          ) : path === '/forgotten' ? (
            <ForgottenPage
              key={user.id}
              query={routeQuery}
              projects={projects}
              tasks={tasks}
              userId={user.id}
              revision={dataRevision.current + canonicalReadRevision.current}
              visible={visible}
              load={loadForgotten}
              dismiss={dismissForgotten}
              navigate={navigate}
              onEditTask={changeTaskEditor}
              onCompleteTask={(task) =>
                updateTask(task.id, { status: 'done', expectedVersion: task.version })
              }
            />
          ) : path === '/notes' || path === '/bookmarks' ? (
            <LibraryPage
              items={items}
              projects={projects}
              kind={path === '/notes' ? 'note' : 'bookmark'}
              navigate={navigate}
              onNewItem={(kind) => changePersonalEditor({ kind, item: null })}
              onEditItem={editPersonalItem}
            />
          ) : path === '/search' ? (
            <SearchPage
              key={`${user.id}-${searchRun}`}
              initialQuery={searchQuery}
              revision={dataRevision.current + searchRevision}
              search={searchWorkspace}
              navigate={navigate}
              onEditTask={(task) => void openSearchTask(task)}
              onEditItem={editPersonalItem}
            />
          ) : tasksPage ? (
            <Tasks
              tasks={tasks}
              projects={projects}
              users={users}
              historyUsers={historyUsers}
              user={user}
              updateTask={updateTask}
              insertCommand={insertCommand}
              setEditor={(task) => (task === 'new' ? openNewTask() : changeTaskEditor(task))}
            />
          ) : newProjectPage ? (
            <ProjectSetup onCreate={createProject} onCancel={() => navigate('/projects')} />
          ) : path === '/projects' ? (
            <Projects
              projects={projects}
              tasks={tasks}
              onOpen={(id) => navigate(`/projects/${id}`)}
              onNew={() => navigate('/projects/new')}
            />
          ) : activeProject ? (
            <ProjectBoard
              key={activeProject.id}
              project={activeProject}
              tasks={tasks.filter((task) => task.projectId === activeProject.id)}
              users={displayUsers}
              onNewTask={(status) => openNewTask(activeProject.id, status)}
              onEditTask={changeTaskEditor}
              updateTask={updateTask}
              onBack={() => navigate('/projects')}
              onDelete={() => requestProjectDelete(activeProject)}
              privateContext={
                <ProjectContext
                  key={activeProject.id}
                  project={activeProject}
                  items={items}
                  tasks={tasks}
                  channels={channels}
                  users={users}
                  userId={user.id}
                  now={clock}
                  expanded={new URLSearchParams(routeQuery).get('context') === '1'}
                  onToggle={() => {
                    const query = new URLSearchParams(routeQuery);
                    if (query.get('context') === '1') query.delete('context');
                    else query.set('context', '1');
                    navigate(`/projects/${activeProject.id}${query.size ? `?${query}` : ''}`);
                  }}
                  loading={loading}
                  syncError={personalSyncError || error}
                  onRefresh={() => void refresh().catch((error) => setError(errorMessage(error)))}
                  onNew={(kind) =>
                    changePersonalEditor({ kind, item: null, projectId: activeProject.id })
                  }
                  onEdit={editPersonalItem}
                  onComplete={(item, completed) =>
                    updatePersonalItem(item.id, { expectedVersion: item.version, completed })
                  }
                  onSnooze={(item) =>
                    updatePersonalItem(item.id, {
                      expectedVersion: item.version,
                      scheduledAt: new Date(Date.now() + 30 * 60000).toISOString(),
                    })
                  }
                  onOpenSource={(source) => void openFollowUpSource(source)}
                />
              }
            />
          ) : path === '/terminal' ? (
            <TerminalPage
              connected={online && !error && !loading}
              entries={entries}
              openAssistant={assistant ? () => setAssistantOpen(true) : undefined}
              insertCommand={insertCommand}
            />
          ) : path === '/shortcuts' ? (
            <Shortcuts insertCommand={insertCommand} />
          ) : path === '/settings' ? (
            <Settings
              notificationAccountCurrent={() =>
                accountGeneration.current === notificationGeneration
              }
              workspace={workspace}
              users={users}
              user={user}
              logout={logout}
              navigate={navigate}
              notify={notify}
            />
          ) : path === '/people' ? (
            <People
              historyUsers={historyUsers}
              users={users}
              user={user}
              navigate={navigate}
              invite={() => navigate('/settings')}
            />
          ) : activeChannel || activePeer ? (
            <Conversation
              followUp={
                activeChannel
                  ? () => createFollowUp({ kind: 'channel', id: activeChannel.id })
                  : activePeer && activePeer.id !== user.id && activePeer.accountStatus === 'active'
                    ? () => createFollowUp({ kind: 'dm', id: activePeer.id })
                    : undefined
              }
              key={activeChannel?.id || activePeer?.id}
              channel={activeChannel}
              peer={activePeer}
              users={users}
              historyUsers={historyUsers}
              user={user}
              context={context}
              runCommand={runCommand}
              acknowledgeMessages={acknowledgeMessages}
              assistantDraft={
                messageDraft &&
                ((messageDraft.channelId && messageDraft.channelId === activeChannel?.id) ||
                  (messageDraft.recipientId && messageDraft.recipientId === activePeer?.id))
                  ? messageDraft
                  : undefined
              }
              consumeAssistantDraft={() => setMessageDraft(null)}
            />
          ) : (
            <Empty
              icon={<TerminalIcon />}
              title="This page isn’t here."
              description="The page or conversation may have moved."
            >
              <Button color="primary" onClick={() => navigate('/tasks')}>
                Back to tasks
                <ArrowRight />
              </Button>
            </Empty>
          )}
        </main>
        <CommandBar
          connected={online && !error && !loading}
          context={context}
          value={command}
          setValue={setCommand}
          run={runCommand}
          history={history}
          inputRef={commandRef}
          busy={commandBusy}
          compact={!!activeChannel || !!activePeer}
        />
      </div>
      {assistantOpen && assistant && (
        <Suspense fallback={null}>
          <Assistant
            key={assistant.id}
            turn={assistant}
            close={() => {
              cancelAssistant();
              setAssistantOpen(false);
            }}
            cancel={cancelAssistant}
            ask={(prompt, scope) => void askAssistant(prompt, scope)}
            apply={applyAssistantProposal}
            proposalContext={assistantProposalContext}
            sourceLabel={assistantSourceLabel}
            openSource={openAssistantSource}
            draftLabel={assistantDraftLabel}
            openDraft={openAssistantDraft}
          />
        </Suspense>
      )}
      {personalEditor && (
        <PersonalEditor
          key={
            personalEditor.item?.id ||
            `${personalEditor.kind}-${personalEditor.projectId || ''}-${personalEditor.source?.kind || ''}-${personalEditor.source?.id || ''}`
          }
          tasks={tasks}
          channels={channels}
          users={users}
          userId={user.id}
          defaultSource={personalEditor.source}
          defaultTitle={personalEditor.title}
          defaultCounterparty={personalEditor.counterparty}
          defaultProjectId={personalEditor.projectId}
          onOpenSource={(source) => void openFollowUpSource(source)}
          onDraftChange={() => followUpOpenSerial.current++}
          item={personalEditor.item}
          kind={personalEditor.kind}
          configureRecurrence={
            personalEditor.item
              ? (recurrence, version) =>
                  configureItemRecurrence(personalEditor.item!.id, recurrence, version)
              : undefined
          }
          openCurrentOccurrence={
            personalEditor.item?.recurrence &&
            items.some(
              (item) =>
                item.recurrence?.id === personalEditor.item!.recurrence!.id && !item.completedAt,
            )
              ? () => {
                  const current = items.find(
                    (item) =>
                      item.recurrence?.id === personalEditor.item!.recurrence!.id &&
                      !item.completedAt,
                  );
                  if (current) editPersonalItem(current);
                }
              : undefined
          }
          latestItem={
            personalEditor.item
              ? items.find((item) => item.id === personalEditor.item!.id)
              : undefined
          }
          projects={projects}
          timeZone={timeZone}
          close={() => changePersonalEditor(null)}
          save={async (payload) => {
            const generation = accountGeneration.current;
            if (personalEditor.item)
              await updatePersonalItem(personalEditor.item.id, payload as PersonalItemPatch);
            else await createPersonalItem(payload as NewPersonalItem);
            if (generation === accountGeneration.current) changePersonalEditor(null);
          }}
          remove={
            personalEditor.item
              ? () => {
                  setPersonalDeleteError('');
                  setPersonalDelete(
                    items.find((item) => item.id === personalEditor.item!.id) ||
                      personalEditor.item,
                  );
                }
              : undefined
          }
        />
      )}
      {personalDelete && (
        <Modal
          title="Delete this private item?"
          close={() => {
            if (!personalDeleteRunning.current) setPersonalDelete(null);
          }}
        >
          <p className="delete-description">
            “{personalDelete.title}” will be permanently deleted from your private items.
            {personalDelete.recurrence?.active &&
              ' This also stops future repeats. Completed occurrences remain in history.'}
          </p>
          {personalDeleteError && <ErrorNotice message={personalDeleteError} />}
          <div className="modal-actions">
            <Button
              color="secondary"
              variant="outline"
              disabled={personalDeleting}
              onClick={() => setPersonalDelete(null)}
            >
              Keep item
            </Button>
            <Button
              color="danger"
              loading={personalDeleting}
              onClick={() => void removePersonalItem()}
            >
              Delete item
            </Button>
          </div>
        </Modal>
      )}
      {editor && (
        <TaskEditor
          followUp={
            editor !== 'new' ? () => createFollowUp({ kind: 'task', id: editor.id }) : undefined
          }
          key={
            typeof editor === 'string'
              ? `${editor}-${newTaskDefaults.projectId}-${newTaskDefaults.status}`
              : editor.id
          }
          task={editor === 'new' ? null : editor}
          latestTask={editor === 'new' ? undefined : tasks.find((task) => task.id === editor.id)}
          configureRecurrence={
            editor !== 'new'
              ? (recurrence, version) => configureTaskRecurrence(editor.id, recurrence, version)
              : undefined
          }
          openCurrentOccurrence={
            editor !== 'new' &&
            editor.recurrence &&
            tasks.some(
              (task) => task.recurrence?.id === editor.recurrence!.id && task.status !== 'done',
            )
              ? () => {
                  const current = tasks.find(
                    (task) =>
                      task.recurrence?.id === editor.recurrence!.id && task.status !== 'done',
                  );
                  if (current) changeTaskEditor(current);
                }
              : undefined
          }
          users={users}
          projects={projects}
          defaultProjectId={newTaskDefaults.projectId}
          historyUsers={historyUsers}
          defaultStatus={newTaskDefaults.status}
          defaultTimeZone={timeZone}
          close={() => changeTaskEditor(null)}
          save={async (payload) => {
            const generation = accountGeneration.current;
            if (editor === 'new') await createTask(payload as NewTask);
            else await updateTask(editor.id, payload);
            if (generation === accountGeneration.current) changeTaskEditor(null);
          }}
          remove={
            editor !== 'new'
              ? () => requestDelete(tasks.find((task) => task.id === editor.id) || editor)
              : undefined
          }
        />
      )}
      {toast && (
        <div className={`toast ${toast.error ? 'toast-error' : ''}`} role="status">
          {!toast.error && <CheckCircle />}
          <span>{toast.text}</span>
          <Button
            color="secondary"
            variant="ghost"
            uniform
            size="xs"
            aria-label="Dismiss notification"
            onClick={() => setToast(null)}
          >
            <CloseBold />
          </Button>
        </div>
      )}
      {channelModal && (
        <ChannelEditor
          close={() => setChannelModal(false)}
          save={async (payload) => {
            const generation = accountGeneration.current;
            const { channel } = await api<{ channel: Channel }>('/channels', 'POST', payload);
            if (generation !== accountGeneration.current) return;
            dataRevision.current++;
            setChannels((previous) => [
              ...previous.filter((existing) => existing.id !== channel.id),
              channel,
            ]);
            setChannelModal(false);
            navigate(`/channels/${channel.id}`);
            notify(`Created #${channel.name}.`);
          }}
        />
      )}
      {deleteRequest && (
        <Modal
          title={deleteRequest.kind === 'project' ? 'Delete this project?' : 'Delete this task?'}
          close={cancelDelete}
          className="delete-modal"
        >
          <p className="delete-description">
            “
            {deleteRequest.kind === 'project'
              ? deleteRequest.project.name
              : deleteRequest.task.title}
            ” will be permanently deleted from this workspace.
          </p>
          {deleteRequest.kind === 'project' && (
            <p className="modal-description">
              Its tasks will be kept in Tasks with their status, assignee, and due date unchanged.
              They will have no project.
            </p>
          )}
          {deleteRequest.kind === 'task' && deleteRequest.task.recurrence?.active && (
            <p className="delete-description">
              Deleting this current occurrence also stops future repeats. Completed occurrences
              remain in history.
            </p>
          )}
          {deleteError && <ErrorNotice message={deleteError} />}
          <div className="modal-actions">
            <Button color="secondary" variant="outline" onClick={cancelDelete} disabled={deleting}>
              {deleteRequest.kind === 'project' ? 'Keep project' : 'Keep task'}
            </Button>
            <Button
              color="danger"
              loading={deleting}
              disabled={deleting}
              onClick={() => void confirmDelete()}
            >
              {deleteRequest.kind === 'project' ? 'Delete project' : 'Delete task'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
function historyPush(path: string) {
  window.history.pushState({}, '', path);
}
function ChannelEditor({
  close,
  save,
}: {
  close: () => void;
  save: (payload: { name: string; description: string }) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError('');
    const fields = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await save({
        name: String(fields.name).trim(),
        description: String(fields.description).trim(),
      });
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title="Create a channel"
      close={() => {
        if (!busy) close();
      }}
    >
      <p className="modal-description">Give your team a shared space for a topic or project.</p>
      <form onSubmit={submit} className="stack-form">
        <Field
          label="Channel name"
          hint="Lowercase letters, numbers, and hyphens. Up to 40 characters."
        >
          <Input
            name="name"
            required
            autoFocus
            maxLength={40}
            pattern="[a-z0-9][a-z0-9-]*"
            placeholder="e.g. launches"
            startAdornment={<span className="muted">#</span>}
            size="lg"
          />
        </Field>
        <Field label="Description (optional)">
          <Input
            name="description"
            maxLength={240}
            placeholder="What’s this channel for?"
            size="lg"
          />
        </Field>
        {error && <ErrorNotice message={error} />}
        <div className="modal-actions">
          <Button color="secondary" variant="outline" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button color="primary" type="submit" loading={busy}>
            Create channel
          </Button>
        </div>
      </form>
    </Modal>
  );
}
